/**
 * Confidence Accelerator pricing: the ONE set of rules for the public page and
 * for the checkout function.
 *
 * KEEP IN SYNC
 * ------------
 * This file exists twice, byte for byte:
 *   src/lib/acceleratorPricing.ts          (site)
 *   website/src/acceleratorPricing.ts      (Cloud Functions)
 * The two TypeScript projects cannot import from each other, so the copy is
 * deliberate. test/accelerator/pricing-sync.test.ts fails the build if they
 * differ. Edit one, copy it over the other.
 *
 * No imports, no Date.now(), no time zone of the machine running it. Every
 * function takes the current time as a number, so tests and the server can
 * pass an exact instant and the page can pass a server-corrected clock.
 *
 * THE TERMS (Scott, Oct 20 2026)
 * ------------------------------
 * Early bird: $597, pay in full only. No early bird payment plan.
 * Window: from enrollment opening through Monday, Nov 30 2026 at 11:59 PM ET.
 * Regular price from Dec 1: $750 pay in full, or the existing $250 x 3 plan
 * (confirmed by Scott Oct 20 11:59 AM ET). The plan is hidden and refused
 * during the early bird and appears on its own after the deadline.
 *
 * WHAT OPENS THE EARLY BIRD
 * -------------------------
 * There is no separate "enrollment open" switch on this site. Enrollment for a
 * class opens when settings/activeClass points at it (Dashboard, Active Class,
 * which writes classStartDate). The early bird is tied to that same control:
 * it is active only while settings/activeClass.classStartDate falls on
 * earlyBird.classStartDateEt. No new toggle.
 *
 * FAIL CLOSED BEFORE THE DEADLINE
 * -------------------------------
 * Before the deadline the only thing on sale is the $597 early bird. If the
 * dashboard class is missing, unreadable or points at another date, nothing is
 * sold (phase 'unavailable'): no $750, no plan. The $750 price and the plan
 * start on Dec 1 and never earlier. The only way to regular pricing before
 * Dec 1 is the earlyBird.enabled kill switch, which needs a code change.
 */

export interface AcceleratorPricingConfig {
  regularPrice: number
  paymentPlan: { enabled: boolean; monthly: number; installments: number }
  earlyBird: {
    enabled: boolean
    price: number
    classStartDateEt: string
    endsAtMs: number
    countdownDays: number
  }
  classDates: { welcomeDay: string; firstLiveClass: string }
}

export const ACCELERATOR_PRICING: AcceleratorPricingConfig = {
  /** Regular pay in full price, whole US dollars. */
  regularPrice: 750,
  paymentPlan: {
    /**
     * Scott confirmed Oct 20 2026 11:59 AM ET: from Dec 1 the existing $250 x 3
     * plan is offered next to $750 pay in full. On means it shows and is
     * accepted automatically once the early bird ends, with no manual step.
     * It is never offered during the early bird, whatever this says.
     */
    enabled: true,
    monthly: 250,
    installments: 3,
  },
  earlyBird: {
    /** Kill switch. False means regular pricing everywhere, immediately. */
    enabled: true,
    price: 597,
    /**
     * The class this early bird sells, as an ET calendar date. Must match the
     * date of settings/activeClass.classStartDate (the Jan 2027 class welcome
     * day). If the dashboard points at any other class before the deadline,
     * checkout is unavailable (never $750, never the plan).
     */
    classStartDateEt: '2027-01-01',
    /**
     * First instant that is NOT early bird: Tue Dec 1 2026 00:00:00 ET.
     * Nov 30 is in EST (UTC-5), so this is 2026-12-01T05:00:00Z.
     * The last early bird second is Mon Nov 30 11:59:59 PM ET.
     */
    endsAtMs: Date.UTC(2026, 11, 1, 5, 0, 0),
    /** Countdown shows only in this final stretch before endsAtMs. */
    countdownDays: 7,
  },
  /** Class dates as Scott gave them, for the class that matches the early bird. */
  classDates: {
    welcomeDay: 'Friday, January 1, 2027',
    firstLiveClass: 'Wednesday, January 6, 2027',
  },
}

export const EARLY_BIRD_BANNER_TEXT = 'Early bird: $597 through Nov 30 (regular $750)'
export const EARLY_BIRD_ENDS_TEXT = 'Early bird ends Monday, Nov 30 at 11:59 PM ET'
export const EARLY_BIRD_SPECIAL_TEXT = 'Early bird special. Regular price $750 from Dec 1.'

/** Shown instead of any price while checkout is unavailable. No price in it. */
export const ACCELERATOR_UNAVAILABLE_SUMMARY = 'Enrollment details on the Accelerator page'
export const ACCELERATOR_UNAVAILABLE_TEXT =
  'Online enrollment is paused for a moment. Please email support@advantagehorsemanship.com and we will help you enroll.'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The America/New_York calendar date of an instant, as YYYY-MM-DD.
 * Uses Intl so it is right on either side of a DST change no matter what time
 * zone the browser or server is in.
 */
export function etDateKey(ms: number): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/**
 * activeClassStartMs:
 *   number     settings/activeClass.classStartDate is known
 *   null       known to be missing or unreadable (server: doc absent)
 *   undefined  not loaded yet (page only). Treated as a match so the page
 *              does not flash the regular price while Firestore loads. The
 *              checkout function never passes undefined.
 */
export function classMatchesEarlyBird(
  activeClassStartMs: number | null | undefined,
  config: AcceleratorPricingConfig = ACCELERATOR_PRICING,
): boolean {
  if (activeClassStartMs === undefined) return true
  if (activeClassStartMs === null || !Number.isFinite(activeClassStartMs)) return false
  return etDateKey(activeClassStartMs) === config.earlyBird.classStartDateEt
}

export interface AcceleratorPriceState {
  /**
   * early_bird   $597 pay in full only.
   * regular      $750 pay in full, plus the plan when enabled. Dec 1 onward,
   *              or any time the earlyBird.enabled kill switch is off.
   * unavailable  Before the deadline but the dashboard class does not match
   *              (missing, unreadable or another date). Nothing is sold.
   */
  phase: 'early_bird' | 'regular' | 'unavailable'
  /** What pay in full costs right now, whole US dollars. */
  price: number
  regularPrice: number
  earlyBirdActive: boolean
  /** False only in the 'unavailable' phase. Pages hide prices and enroll buttons. */
  checkoutAvailable: boolean
  /** True only in the last countdownDays before the deadline. */
  showCountdown: boolean
  /** Milliseconds left in the early bird, or null when not in the early bird. */
  msRemaining: number | null
  /** Show and sell the $250 x 3 plan. Never during the early bird. */
  paymentPlanVisible: boolean
  /** True when the dashboard class is the one with the published class dates. */
  showClassDates: boolean
  earlyBirdEndsAtMs: number
}

export function getAcceleratorPriceState(
  nowMs: number,
  activeClassStartMs: number | null | undefined,
  config: AcceleratorPricingConfig = ACCELERATOR_PRICING,
): AcceleratorPriceState {
  const eb = config.earlyBird
  const matches = classMatchesEarlyBird(activeClassStartMs, config)
  // A clock that is not a finite number is never treated as "after Dec 1".
  const beforeDeadline = !Number.isFinite(nowMs) || nowMs < eb.endsAtMs
  const inEarlyBirdWindow = eb.enabled === true && beforeDeadline
  const earlyBirdActive = inEarlyBirdWindow && Number.isFinite(nowMs) && matches
  const unavailable = inEarlyBirdWindow && !earlyBirdActive
  const msRemaining = earlyBirdActive ? eb.endsAtMs - nowMs : null
  return {
    phase: earlyBirdActive ? 'early_bird' : unavailable ? 'unavailable' : 'regular',
    price: earlyBirdActive ? eb.price : config.regularPrice,
    regularPrice: config.regularPrice,
    earlyBirdActive,
    checkoutAvailable: !unavailable,
    showCountdown: msRemaining !== null && msRemaining <= eb.countdownDays * DAY_MS,
    msRemaining,
    paymentPlanVisible: !inEarlyBirdWindow && config.paymentPlan.enabled === true,
    showClassDates: matches && activeClassStartMs !== undefined,
    earlyBirdEndsAtMs: eb.endsAtMs,
  }
}

/** One line for cards elsewhere on the site (home, /start). */
export function acceleratorPriceSummary(state: AcceleratorPriceState): string {
  if (!state.checkoutAvailable) return ACCELERATOR_UNAVAILABLE_SUMMARY
  if (state.earlyBirdActive) {
    return `$${state.price} early bird, pay in full (regular $${state.regularPrice})`
  }
  return state.paymentPlanVisible
    ? `$${state.regularPrice} pay in full or $${ACCELERATOR_PRICING.paymentPlan.monthly}/month x ${ACCELERATOR_PRICING.paymentPlan.installments}`
    : `$${state.regularPrice} pay in full`
}

export interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function countdownParts(ms: number): CountdownParts {
  const total = Math.max(0, Math.floor(ms / 1000))
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}
